import React, { useState, useEffect } from "react";
import { Calendar } from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
  startOfWeek,
  endOfWeek,
  isWithinInterval,
  parse as parseDateFns,
} from "date-fns";
import Navbar from "./Navbar";
import AddReminderForm from "./AddReminderForm";
import { FaClipboardList } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../config/axios";
import ProgressTrackerCard from "./ProgressTrackerCard";
import DashboardStatsCards from "./DashboardStatsCards";
import DashboardMonthlyCharts from "./DashboardMonthlyCharts";
import DashboardPerformers from "./DashboardPerformers";
import { useRole } from "../context/RoleContext";

const Dashboard = () => {
  const { isAdmin, isAgent, isSuperUser, isBroker, firebaseUser, appUser } =
    useRole();
  const isOfficeUser = isAdmin || isSuperUser || isBroker || Boolean(firebaseUser);
  const agentWelcomeName =
    appUser?.displayName ||
    [appUser?.firstName, appUser?.lastName].filter(Boolean).join(" ") ||
    appUser?.email ||
    "";
  const [reminders, setReminders] = useState([]);
  const [pendingPasswordCount, setPendingPasswordCount] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchReminders = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch reminders from the API
      const res = await axiosInstance.get(`/reminders`);
      setReminders(res.data);
    } catch (error) {
      console.error("Error fetching reminders:", error);

      // More specific error messages
      if (error.code === "ECONNABORTED") {
        setError(
          "Request timed out. Please check your connection and try again.",
        );
      } else if (error.response) {
        // Server responded with error status
        setError(
          `Server error: ${error.response.status}. Please try again later.`,
        );
      } else if (error.request) {
        // Request was made but no response received
        setError(
          "Unable to connect to server. Please check if the backend is running.",
        );
      } else {
        setError("Failed to fetch reminders. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReminders();
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      setPendingPasswordCount(0);
      return;
    }
    axiosInstance
      .get("/auth/password-reset-requests?status=pending")
      .then((res) => setPendingPasswordCount(res.data.pendingCount || 0))
      .catch(() => setPendingPasswordCount(0));
  }, [isAdmin]);

  const formatDate = (dateObj) => {
    if (!dateObj) return "";
    const d = new Date(dateObj);
    if (isNaN(d.getTime())) return "";
    return [
      ("0" + d.getDate()).slice(-2),
      ("0" + (d.getMonth() + 1)).slice(-2),
      d.getFullYear(),
    ].join("/");
  };

  const selectedDateStr = selectedDate ? formatDate(selectedDate) : "";

  const parseReminderDate = (dateStr) => {
    return parseDateFns(dateStr, "dd/MM/yyyy", new Date());
  };

  const getCurrentWeekRange = () => {
    const today = new Date();
    return {
      start: startOfWeek(today, { weekStartsOn: 1 }),
      end: endOfWeek(today, { weekStartsOn: 1 }),
    };
  };

  const handleReminderAdded = () => {
    fetchReminders();
  };

  const handleClearReminders = () => {
    setReminders([]);
  };

  const currentWeekRange = getCurrentWeekRange();
  const weeklyFilteredReminders = reminders.filter((rem) => {
    try {
      const reminderDateObj = parseReminderDate(rem.date);
      return isWithinInterval(reminderDateObj, currentWeekRange);
    } catch (e) {
      console.error("Error parsing reminder date for filtering:", rem.date, e);
      return false;
    }
  });

  const dailyFilteredReminders = selectedDate
    ? weeklyFilteredReminders.filter((rem) => rem.date === selectedDateStr)
    : weeklyFilteredReminders;

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
          role="alert"
        >
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
          <div className="mt-3 space-x-2">
            <button
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
              onClick={fetchReminders}
            >
              Retry
            </button>
            <button
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {Navbar ? <Navbar /> : null}
      {isAgent && (
        <div className="px-6 pt-6">
          <h1 className="text-2xl font-semibold text-gray-900">
            Welcome{agentWelcomeName ? `, ${agentWelcomeName}` : ""}
          </h1>
        </div>
      )}
      {isAdmin && pendingPasswordCount > 0 && (
        <div className="mx-6 mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-amber-900">
            <strong>{pendingPasswordCount}</strong> password reset
            {pendingPasswordCount === 1 ? " request is" : " requests are"}{" "}
            waiting. Resend the login email from User Access.
          </p>
          <button
            type="button"
            onClick={() => navigate("/user-access")}
            className="bg-blue-900 text-white text-sm px-4 py-2 rounded hover:bg-blue-800 flex-shrink-0"
          >
            Review and resend
          </button>
        </div>
      )}
      <div className="flex flex-col xl:flex-row px-6 pt-4 pb-3 gap-4">
        {/* Calendar Section */}
        <div className="xl:flex-1 bg-sky-50/80 rounded-xl shadow-sm border border-sky-100 p-3 min-w-0">
          <h2 className="text-sm font-semibold mb-2 text-center text-sky-900">
            Calendar
          </h2>

          {Calendar ? (
          <Calendar
            onChange={setSelectedDate}
            value={selectedDate}
            className="dashboard-calendar-compact mx-auto border-none"
            tileClassName={({ date }) =>
              formatDate(date) === selectedDateStr
                ? "bg-yellow-300 rounded-lg"
                : ""
            }
          />
          ) : null}
        </div>

        {/* Reminder Section */}
        <div className="xl:flex-1 bg-emerald-50/80 rounded-xl shadow-sm border border-emerald-100 p-3 min-w-0">
          <h2 className="text-sm font-semibold mb-2 text-emerald-900">
            Add Reminder
          </h2>
          {AddReminderForm ? (
            <AddReminderForm onReminderAdded={handleReminderAdded} />
          ) : null}

          <div className="mt-3">
            <div className="flex justify-between items-center gap-2 mb-2">
              <h3 className="text-xs font-semibold text-gray-700">
                {selectedDate
                  ? `Reminders for ${selectedDateStr} (This Week)`
                  : "This Week's Reminders"}
              </h3>
              {isOfficeUser && (
              <button
                onClick={() => navigate("/database/reminders")}
                className="bg-green-500 text-white px-2 py-0.5 rounded hover:bg-green-600 text-xs flex-shrink-0"
              >
                View All Reminders
              </button>
              )}
            </div>

            {isLoading && (
              <div className="flex items-center py-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                <span className="ml-2 text-xs">Loading reminders...</span>
              </div>
            )}

            {!isLoading && dailyFilteredReminders.length === 0 && (
              <p className="text-xs text-gray-500">
                {selectedDate
                  ? `No reminders for ${selectedDateStr} this week.`
                  : "No reminders for this week."}
              </p>
            )}

            {!isLoading && dailyFilteredReminders.length > 0 && (
              <ul className="space-y-1 mb-2">
                {dailyFilteredReminders.map((reminder) => (
                  <li
                    key={reminder._id}
                    className="px-2 py-1 bg-gray-100 rounded-md"
                  >
                    <p className="text-xs font-semibold text-gray-800">
                      {reminder.date}
                    </p>
                    <p className="text-xs text-gray-600">{reminder.reminder}</p>
                  </li>
                ))}
              </ul>
            )}

            {weeklyFilteredReminders.length > 0 && !isLoading && (
              <div className="space-x-2">
                <button
                  onClick={handleClearReminders}
                  className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 text-xs"
                >
                  Clear Displayed Weekly Reminders
                </button>
                <button
                  onClick={fetchReminders}
                  className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 text-xs"
                >
                  Refresh All Reminders
                </button>
              </div>
            )}
          </div>
        </div>

        {!isAgent && ProgressTrackerCard ? <ProgressTrackerCard /> : null}
      </div>

      {(isOfficeUser || isAgent) && DashboardStatsCards ? (
        <DashboardStatsCards />
      ) : null}
      {(isOfficeUser || isAgent) && DashboardMonthlyCharts ? (
        <DashboardMonthlyCharts />
      ) : null}
      {!isAgent && DashboardPerformers ? <DashboardPerformers /> : null}

      {isSuperUser && (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6 px-6 pb-10">
        <div
          className="bg-white shadow-md hover:shadow-lg transition rounded-lg p-6 flex flex-col items-center cursor-pointer"
          onClick={() => navigate("/draft-review")}
        >
          {FaClipboardList ? (
            <FaClipboardList className="text-4xl text-blue-900 mb-2" />
          ) : null}
          <h3 className="text-lg font-semibold text-black text-center">
            Review Drafts
          </h3>
        </div>
      </div>
      )}
    </div>
  );
};

export default Dashboard;
